import { createSignal, createEffect, For, Show, onCleanup } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import { useSFetch } from '../hooks/sfetch';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useTenantPermission } from '../hooks/tenantPermissions';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { TenantRole } from '../models/Tenant';
import StateDisplay from '../components/StateDisplay';
import Text from '../components/Text';
import styles from './Tenants.module.css';
import { firstError } from '../api/envelope';

export default function NewTenantMember() {
    const sfetch = useSFetch();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { userTenant } = useUserContext();
    const { hasRole } = useTenantPermission();

    const tenantId = () => userTenant()?.tenantId ?? null;
    const canManage = () => hasRole(TenantRole.Admin);

    const [existingMembers, setExistingMembers] = createSignal<{ userId: string }[]>([]);
    const [search, setSearch] = createSignal('');
    const [results, setResults] = createSignal<{ id: string; username: string }[]>([]);
    const [pickedUserId, setPickedUserId] = createSignal('');
    const [role, setRole] = createSignal<TenantRole>(TenantRole.Member);
    const [adding, setAdding] = createSignal(false);

    createEffect(() => {
        const id = tenantId();
        if (!id) return;
        void (async () => {
            const res = await api.tenant.getMembers(id);
            if (isOk(res.data)) setExistingMembers((res.data.value.members ?? []) as { userId: string }[]);
        })();
    });

    let debounceHandle: number | undefined;
    createEffect(() => {
        const term = search().trim();
        if (debounceHandle) window.clearTimeout(debounceHandle);
        if (!term || pickedUserId()) { setResults([]); return; }
        debounceHandle = window.setTimeout(async () => {
            const res = await sfetch('/api/User/Users');
            if (res.ok && res.value) {
                const existing = new Set(existingMembers().map(m => m.userId));
                const matches = (res.value as { id: string; username: string }[])
                    .filter(u => u.username.toLowerCase().includes(term.toLowerCase()))
                    .filter(u => !existing.has(u.id))
                    .slice(0, 10);
                setResults(matches);
            }
        }, 200);
    });
    onCleanup(() => { if (debounceHandle) window.clearTimeout(debounceHandle); });

    function pickUser(u: { id: string; username: string }) {
        setPickedUserId(u.id);
        setSearch(u.username);
        setResults([]);
    }

    async function submit(e: Event) {
        e.preventDefault();
        const id = tenantId();
        if (!id || !pickedUserId()) return;
        setAdding(true);
        try {
            const res = await api.tenant.addMember(id, { userId: pickedUserId(), role: role() });
            if (isOk(res.data)) {
                toast?.info(t('TENANT_MEMBER_ADDED'));
                navigate('/tenant/members');
            } else {
                toast?.error(firstError(res.data) ?? t('TENANT_MEMBER_ADD_FAILED'));
            }
        } finally {
            setAdding(false);
        }
    }

    return (
        <div class={styles.formContainer}>
            <A href="/tenant/members" class={styles.backLink}>
                <i class="fa-solid fa-arrow-left"></i>
                <Text val="TENANT_BACK_TO_MEMBERS" />
            </A>

            <Show when={!tenantId()}>
                <StateDisplay empty icon="fa-solid fa-users-slash" message="TENANT_NO_SELECTION" />
            </Show>

            <Show when={tenantId() && !canManage()}>
                <StateDisplay empty icon="fa-solid fa-lock" message="TENANT_GRANTS_NEED_ADMIN" />
            </Show>

            <Show when={tenantId() && canManage()}>
                <div class={styles.header}>
                    <div class={styles.titleSection}>
                        <div class={styles.cardAvatar}>
                            <i class="fa-solid fa-user-plus"></i>
                        </div>
                        <div class={styles.titleText}>
                            <h1><Text val="TENANT_ADD_MEMBER" /></h1>
                            <p class={styles.subtitle}>{userTenant()?.tenant?.name}</p>
                        </div>
                    </div>
                </div>

                <form class={styles.formCard} onSubmit={submit}>
                    <div class={styles.formField}>
                        <span class={styles.fieldLabel}><Text val="TENANT_USERNAME" /></span>
                        <input
                            type="text"
                            class={styles.input}
                            placeholder={t('TENANT_SEARCH_USERNAME')}
                            value={search()}
                            onInput={e => {
                                setSearch(e.currentTarget.value);
                                setPickedUserId('');
                            }}
                        />
                        <Show when={results().length > 0}>
                            <div class={styles.searchResults}>
                                <For each={results()}>
                                    {u => (
                                        <div
                                            class={`${styles.searchResult}${pickedUserId() === u.id ? ` ${styles.selected}` : ''}`}
                                            onClick={() => pickUser(u)}
                                        >
                                            <i class="fa-solid fa-user"></i>
                                            <span>{u.username}</span>
                                        </div>
                                    )}
                                </For>
                            </div>
                        </Show>
                        <Show when={pickedUserId()}>
                            <span class={styles.fieldHelp}>
                                <Text val="TENANT_SELECTED" /> {search()}
                            </span>
                        </Show>
                    </div>

                    <label class={styles.formField}>
                        <span class={styles.fieldLabel}><Text val="TENANT_ROLE" /></span>
                        <select
                            class={styles.select}
                            value={role()}
                            onChange={e => setRole(parseInt(e.currentTarget.value) as TenantRole)}
                        >
                            <option value={TenantRole.Member}>{t('TENANT_ROLE_MEMBER')}</option>
                            <option value={TenantRole.Admin}>{t('TENANT_ROLE_ADMIN')}</option>
                            <option value={TenantRole.Owner}>{t('TENANT_ROLE_OWNER')}</option>
                        </select>
                    </label>

                    <div class={styles.formActions}>
                        <A href="/tenant/members" class="btn btn-secondary"><Text val="CANCEL" /></A>
                        <button type="submit" class="btn btn-primary" disabled={!pickedUserId() || adding()}>
                            <Show when={adding()} fallback={<i class="fa-solid fa-plus" />}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                            <Text val="TENANT_ADD" />
                        </button>
                    </div>
                </form>
            </Show>
        </div>
    );
}
